import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import Modal from "@/components/admin/Modal";
import { Field, TextInput, TextArea, Select, Toggle } from "@/components/admin/FormControls";
import ImageUploader from "@/components/admin/ImageUploader";
import FileUploader from "@/components/admin/FileUploader";
import VimeoPicker from "@/components/admin/VimeoPicker";
import { Plus, Pencil, Trash2, Clock, ChevronDown, ChevronUp, X, BookOpen, Film } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (url) => !url ? "" : (url.startsWith("http") ? url : `${BACKEND}${url}`);

function inferResourceType(filename) {
    const ext = (filename || "").toLowerCase().split(".").pop();
    if (["mp3", "wav", "m4a", "ogg", "aac"].includes(ext)) return "meditation";
    if (["epub"].includes(ext)) return "ebook";
    return "pdf";
}
function stripExt(name) {
    return (name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
}

const emptyJourney = {
    title: "", subtitle: "", description: "",
    mentor_id: "", category_id: "",
    cover: "", slug: "",
    is_featured: true, is_free: false,
    modules: [],
};

const emptyLesson = () => ({
    order: 1, title: "", description: "", video_url: "", duration_minutes: 12, resources: [],
});
const emptyModule = () => ({
    order: 1, title: "", description: "", lessons: [emptyLesson()],
});

export default function AdminJourneys() {
    const [items, setItems] = useState([]);
    const [mentors, setMentors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyJourney);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState({}); // module idx -> bool
    const [lessonExpanded, setLessonExpanded] = useState({}); // `${mi}-${li}` -> bool

    const load = async () => {
        const [j, m, c] = await Promise.all([
            api.get("/admin/journeys"),
            api.get("/admin/mentors"),
            api.get("/admin/categories"),
        ]);
        setItems(j.data); setMentors(m.data); setCategories(c.data);
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setForm({ ...emptyJourney, modules: [{ ...emptyModule(), order: 1 }] });
        setEditing(null); setError(""); setExpanded({ 0: true }); setLessonExpanded({ "0-0": true }); setOpen(true);
    };

    const openEdit = async (id) => {
        const { data } = await api.get(`/admin/journeys/${id}`);
        setForm({
            ...emptyJourney,
            ...data,
            modules: (data.modules || []).map(m => ({
                ...m,
                lessons: (m.lessons || []).map(l => ({ ...l, resources: l.resources || [] })),
            })),
        });
        setEditing(id); setError(""); setExpanded({}); setLessonExpanded({}); setOpen(true);
    };

    // ---- Module helpers ----
    const addModule = () => {
        const order = (form.modules.length || 0) + 1;
        setForm({ ...form, modules: [...form.modules, { ...emptyModule(), order }] });
        setExpanded({ ...expanded, [form.modules.length]: true });
    };
    const updateModule = (i, patch) => {
        const next = form.modules.map((m, idx) => idx === i ? { ...m, ...patch } : m);
        setForm({ ...form, modules: next });
    };
    const removeModule = (i) => {
        if (!window.confirm("Excluir este módulo (e todas as aulas dentro)?")) return;
        const next = form.modules.filter((_, idx) => idx !== i).map((m, idx) => ({ ...m, order: idx + 1 }));
        setForm({ ...form, modules: next });
    };
    const moveModule = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= form.modules.length) return;
        const next = [...form.modules];
        [next[i], next[j]] = [next[j], next[i]];
        next.forEach((m, idx) => m.order = idx + 1);
        setForm({ ...form, modules: next });
    };

    // ---- Lesson helpers ----
    const addLesson = (mi) => {
        const m = form.modules[mi];
        const lessons = [...(m.lessons || []), { ...emptyLesson(), order: (m.lessons?.length || 0) + 1 }];
        updateModule(mi, { lessons });
        setLessonExpanded({ ...lessonExpanded, [`${mi}-${lessons.length - 1}`]: true });
    };
    const updateLesson = (mi, li, patch) => {
        const m = form.modules[mi];
        const lessons = (m.lessons || []).map((l, idx) => idx === li ? { ...l, ...patch } : l);
        updateModule(mi, { lessons });
    };
    const removeLesson = (mi, li) => {
        const m = form.modules[mi];
        const lessons = (m.lessons || []).filter((_, idx) => idx !== li).map((l, idx) => ({ ...l, order: idx + 1 }));
        updateModule(mi, { lessons });
    };
    const moveLesson = (mi, li, dir) => {
        const m = form.modules[mi];
        const arr = [...(m.lessons || [])];
        const j = li + dir;
        if (j < 0 || j >= arr.length) return;
        [arr[li], arr[j]] = [arr[j], arr[li]];
        arr.forEach((l, idx) => l.order = idx + 1);
        updateModule(mi, { lessons: arr });
    };

    // ---- Resource helpers (per lesson) ----
    const addResource = (mi, li) => {
        const l = form.modules[mi].lessons[li];
        updateLesson(mi, li, { resources: [...(l.resources || []), { type: "pdf", title: "", url: "" }] });
    };
    const updateResource = (mi, li, ri, patch) => {
        const l = form.modules[mi].lessons[li];
        const r = l.resources.map((x, idx) => idx === ri ? { ...x, ...patch } : x);
        updateLesson(mi, li, { resources: r });
    };
    const removeResource = (mi, li, ri) => {
        const l = form.modules[mi].lessons[li];
        updateLesson(mi, li, { resources: l.resources.filter((_, idx) => idx !== ri) });
    };

    const save = async () => {
        setError("");
        try {
            const body = { ...form };
            if (!body.slug) delete body.slug;
            if (editing) await api.put(`/admin/journeys/${editing}`, body);
            else await api.post("/admin/journeys", body);
            setOpen(false);
            await load();
        } catch (e) {
            setError(formatApiError(e));
            setTimeout(() => {
                document.querySelector('[data-testid="journey-error"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 50);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Excluir esta jornada? O progresso dos usuários também será removido.")) return;
        try { await api.delete(`/admin/journeys/${id}`); await load(); }
        catch (e) { alert(formatApiError(e)); }
    };

    // Stats helpers
    const totalLessons = form.modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);
    const totalMinutes = form.modules.reduce((a, m) => a + (m.lessons || []).reduce((b, l) => b + (Number(l.duration_minutes) || 0), 0), 0);

    const [repairing, setRepairing] = useState(false);
    const repairVimeo = async () => {
        if (!window.confirm("Buscar no Vimeo os hashes corretos de todas as aulas? (Recomendado se algum aluno reclamou de 'Faça login no Vimeo'.)")) return;
        setRepairing(true);
        try {
            const { data } = await api.post("/admin/vimeo/repair-urls");
            const lines = (data.details || []).flatMap(d => [
                `📘 ${d.journey}: ${d.fixed.length} corrigida(s)`,
                ...d.fixed.map(f => `   ✓ ${f.lesson}`),
                ...d.errors.map(e => `   ⚠ ${e.lesson}: ${e.error}`),
            ]);
            alert(`Concluído. ${data.updated_journeys} jornada(s) atualizada(s).\n\n${lines.join("\n") || "Nenhuma URL precisava de correção."}`);
            await load();
        } catch (e) { alert(formatApiError(e)); }
        finally { setRepairing(false); }
    };

    return (
        <AdminLayout
            eyebrow="Conteúdo"
            title="Jornadas"
            actions={
                <>
                    <button onClick={repairVimeo} disabled={repairing} data-testid="journey-repair-vimeo" className="btn-ghost">
                        {repairing ? "Reparando…" : "Reparar links Vimeo"}
                    </button>
                    <button onClick={openCreate} data-testid="journey-create-btn" className="btn-primary"><Plus className="w-4 h-4" /> Nova jornada</button>
                </>
            }
        >
            <div data-testid="journeys-table" className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <table className="w-full">
                    <thead className="bg-[#FAFAF7] text-left">
                        <tr>
                            <th className="overline text-[#5C5C5C] px-6 py-4">Jornada</th>
                            <th className="overline text-[#5C5C5C] px-6 py-4 hidden md:table-cell">Mentor</th>
                            <th className="overline text-[#5C5C5C] px-6 py-4 hidden md:table-cell">Categoria</th>
                            <th className="overline text-[#5C5C5C] px-6 py-4 hidden lg:table-cell">Módulos · Aulas</th>
                            <th className="overline text-[#5C5C5C] px-6 py-4 hidden lg:table-cell">Status</th>
                            <th className="px-6 py-4 w-32 text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(j => {
                            const lessonCount = (j.modules || []).reduce((a, m) => a + (m.lessons?.length || 0), 0);
                            return (
                                <tr key={j.id} data-testid={`journey-row-${j.id}`} className="border-t border-black/5">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            {j.cover ? (
                                                <img src={resolveSrc(j.cover)} alt="" className="w-14 h-14 object-cover rounded-lg" />
                                            ) : (
                                                <div className="w-14 h-14 rounded-lg bg-[#F8F8F5] border border-black/5" />
                                            )}
                                            <div>
                                                <div className="font-display">{j.title}</div>
                                                <div className="font-body text-xs text-[#5C5C5C] line-clamp-1">{j.subtitle}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-body text-sm text-[#5C5C5C] hidden md:table-cell">{j.mentor_name}</td>
                                    <td className="px-6 py-4 font-body text-sm text-[#5C5C5C] hidden md:table-cell">{j.category_name}</td>
                                    <td className="px-6 py-4 font-body text-sm text-[#5C5C5C] hidden lg:table-cell">{j.modules?.length || 0} módulos · {lessonCount} aulas · {Math.round((j.total_minutes || 0) / 60 * 10) / 10}h</td>
                                    <td className="px-6 py-4 hidden lg:table-cell">
                                        <div className="flex gap-1.5 flex-wrap">
                                            {j.is_featured && <span className="text-[10px] tracking-widest uppercase font-display px-2 py-1 rounded-full bg-[#1A1A1A] text-white">Destaque</span>}
                                            {j.is_free && <span className="text-[10px] tracking-widest uppercase font-display px-2 py-1 rounded-full bg-[#00D4D4]/15 text-[#1A1A1A]">Grátis</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right whitespace-nowrap">
                                        <button onClick={() => openEdit(j.id)} data-testid={`journey-edit-${j.id}`} className="inline-flex items-center gap-1 text-sm text-[#1A1A1A] hover:text-black mr-3"><Pencil className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => remove(j.id)} data-testid={`journey-delete-${j.id}`} className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </td>
                                </tr>
                            );
                        })}
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="px-6 py-12 text-center font-body text-[#5C5C5C]">Nenhuma jornada.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar jornada" : "Nova jornada"} size="xl">
                <div className="space-y-7">
                    <div className="grid md:grid-cols-2 gap-5">
                        <Field label="Título"><TextInput testId="journey-title" value={form.title} onChange={v => setForm({ ...form, title: v })} /></Field>
                        <Field label="Slug" hint="Vazio = gerado automaticamente"><TextInput testId="journey-slug" value={form.slug} onChange={v => setForm({ ...form, slug: v })} /></Field>
                    </div>
                    <Field label="Subtítulo"><TextInput testId="journey-subtitle" value={form.subtitle} onChange={v => setForm({ ...form, subtitle: v })} /></Field>
                    <Field label="Descrição"><TextArea testId="journey-description" value={form.description} onChange={v => setForm({ ...form, description: v })} rows={3} /></Field>
                    <div className="grid md:grid-cols-2 gap-5">
                        <Field label="Mentor">
                            <Select testId="journey-mentor" value={form.mentor_id} onChange={v => setForm({ ...form, mentor_id: v })}
                                    options={mentors.map(m => ({ value: m.id, label: m.name }))} />
                        </Field>
                        <Field label="Categoria">
                            <Select testId="journey-category" value={form.category_id} onChange={v => setForm({ ...form, category_id: v })}
                                    options={categories.map(c => ({ value: c.id, label: c.name }))} />
                        </Field>
                    </div>
                    <Field label="Cover" hint="Imagem de capa da jornada — comprimida automaticamente.">
                        <ImageUploader
                            testId="journey-cover-uploader"
                            value={form.cover}
                            onChange={(url) => setForm({ ...form, cover: url })}
                            aspect="aspect-[16/10]"
                        />
                    </Field>
                    <div className="flex flex-wrap gap-6">
                        <Toggle testId="journey-featured" checked={form.is_featured} onChange={v => setForm({ ...form, is_featured: v })} label="Em destaque" />
                        <Toggle testId="journey-free" checked={form.is_free} onChange={v => setForm({ ...form, is_free: v })} label="Acesso gratuito" />
                    </div>

                    {/* Modules → Lessons editor */}
                    <div>
                        <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
                            <div>
                                <div className="overline text-[#5C5C5C]">Conteúdo da jornada</div>
                                <h3 className="font-display text-xl mt-1">{form.modules.length} módulo(s) · {totalLessons} aula(s) · {totalMinutes} min</h3>
                            </div>
                            <button type="button" onClick={addModule} data-testid="module-add" className="btn-primary !py-2"><Plus className="w-4 h-4" /> Adicionar Módulo</button>
                        </div>

                        <div className="space-y-3">
                            {form.modules.map((m, mi) => {
                                const isOpen = !!expanded[mi];
                                const mLessons = m.lessons || [];
                                const mMinutes = mLessons.reduce((a, l) => a + (Number(l.duration_minutes) || 0), 0);
                                return (
                                    <div key={mi} data-testid={`module-card-${mi}`} className="rounded-2xl border border-black/10 bg-white shadow-sm">
                                        {/* Module header */}
                                        <div className="flex items-center gap-3 p-3 border-b border-black/5">
                                            <button type="button" onClick={() => setExpanded({ ...expanded, [mi]: !isOpen })} className="w-8 h-8 rounded-lg bg-[#FAFAF7] border border-black/5 flex items-center justify-center">
                                                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            </button>
                                            <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center font-display text-sm">
                                                {String(m.order).padStart(2, "0")}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="overline text-[#5C5C5C]">Módulo</div>
                                                <div className="font-display text-base line-clamp-1">
                                                    {m.title || <span className="text-[#5C5C5C] italic">Sem título</span>}
                                                </div>
                                                <div className="font-body text-xs text-[#5C5C5C] flex items-center gap-2 mt-0.5">
                                                    <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> {mLessons.length} aula(s)</span>
                                                    <span>·</span>
                                                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {mMinutes} min</span>
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => moveModule(mi, -1)} className="text-xs text-[#5C5C5C] hover:text-[#1A1A1A] px-2">↑</button>
                                            <button type="button" onClick={() => moveModule(mi, +1)} className="text-xs text-[#5C5C5C] hover:text-[#1A1A1A] px-2">↓</button>
                                            <button type="button" onClick={() => removeModule(mi)} data-testid={`module-delete-${mi}`} className="text-red-600 hover:text-red-700 p-1"><X className="w-4 h-4" /></button>
                                        </div>

                                        {isOpen && (
                                            <div className="p-4 grid gap-4">
                                                <div className="grid md:grid-cols-2 gap-3">
                                                    <Field label="Título do módulo"><TextInput testId={`module-title-${mi}`} value={m.title} onChange={v => updateModule(mi, { title: v })} /></Field>
                                                    <Field label="Descrição curta"><TextInput testId={`module-desc-${mi}`} value={m.description} onChange={v => updateModule(mi, { description: v })} /></Field>
                                                </div>

                                                {/* Lessons */}
                                                <div>
                                                    <div className="flex items-center justify-between mb-3 mt-2">
                                                        <div className="overline text-[#5C5C5C]">Aulas deste módulo</div>
                                                        <button type="button" onClick={() => addLesson(mi)} data-testid={`lesson-add-${mi}`} className="btn-ghost !py-2 !px-3 text-xs"><Plus className="w-3.5 h-3.5" /> Adicionar Aula</button>
                                                    </div>

                                                    <div className="space-y-2">
                                                        {mLessons.length === 0 && (
                                                            <div className="text-center p-6 rounded-xl bg-[#FAFAF7] border border-dashed border-black/15 text-[#5C5C5C] font-body text-sm">
                                                                Nenhuma aula. Clique em "Adicionar Aula" acima.
                                                            </div>
                                                        )}
                                                        {mLessons.map((l, li) => {
                                                            const lKey = `${mi}-${li}`;
                                                            const lOpen = !!lessonExpanded[lKey];
                                                            return (
                                                                <div key={li} data-testid={`lesson-card-${mi}-${li}`} className="rounded-xl border border-black/5 bg-[#FAFAF7]">
                                                                    <div className="flex items-center gap-3 p-3">
                                                                        <button type="button" onClick={() => setLessonExpanded({ ...lessonExpanded, [lKey]: !lOpen })} className="w-7 h-7 rounded-md bg-white border border-black/5 flex items-center justify-center">
                                                                            {lOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                                        </button>
                                                                        <div className="w-9 text-center font-display text-xs text-[#5C5C5C]">{m.order}.{l.order}</div>
                                                                        <Film className="w-4 h-4 text-[#5C5C5C] shrink-0" />
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="font-display text-sm line-clamp-1">{l.title || <span className="text-[#5C5C5C] italic">Sem título</span>}</div>
                                                                            <div className="font-body text-xs text-[#5C5C5C] flex items-center gap-1"><Clock className="w-3 h-3" /> {l.duration_minutes} min</div>
                                                                        </div>
                                                                        <button type="button" onClick={() => moveLesson(mi, li, -1)} className="text-xs text-[#5C5C5C] hover:text-[#1A1A1A] px-2">↑</button>
                                                                        <button type="button" onClick={() => moveLesson(mi, li, +1)} className="text-xs text-[#5C5C5C] hover:text-[#1A1A1A] px-2">↓</button>
                                                                        <button type="button" onClick={() => removeLesson(mi, li)} data-testid={`lesson-delete-${mi}-${li}`} className="text-red-600 hover:text-red-700 p-1"><X className="w-4 h-4" /></button>
                                                                    </div>

                                                                    {lOpen && (
                                                                        <div className="p-3 pt-0 grid gap-3">
                                                                            <div className="grid md:grid-cols-[1fr_120px] gap-3">
                                                                                <Field label="Título"><TextInput testId={`lesson-title-${mi}-${li}`} value={l.title} onChange={v => updateLesson(mi, li, { title: v })} /></Field>
                                                                                <Field label="Duração (min)"><TextInput testId={`lesson-duration-${mi}-${li}`} type="number" min={1} max={240} value={l.duration_minutes} onChange={v => updateLesson(mi, li, { duration_minutes: v })} /></Field>
                                                                            </div>
                                                                            <Field label="Descrição"><TextArea testId={`lesson-desc-${mi}-${li}`} value={l.description} onChange={v => updateLesson(mi, li, { description: v })} rows={2} /></Field>

                                                                            <Field label="Vídeo" hint="Use o Vimeo para todo o conteúdo — ou cole URL do YouTube/MP4 direto abaixo.">
                                                                                <div className="space-y-3">
                                                                                    <VimeoPicker
                                                                                        testId={`lesson-vimeo-${mi}-${li}`}
                                                                                        value={l.video_url}
                                                                                        onChange={(url, meta) => {
                                                                                            const patch = { video_url: url };
                                                                                            if (meta?.duration) patch.duration_minutes = Math.max(1, Math.round(meta.duration / 60));
                                                                                            updateLesson(mi, li, patch);
                                                                                        }}
                                                                                    />
                                                                                    <details className="group">
                                                                                        <summary className="cursor-pointer text-[11px] font-display text-[#5C5C5C] hover:text-[#1A1A1A]">Ou colar URL manual (YouTube / MP4) →</summary>
                                                                                        <div className="mt-2">
                                                                                            <TextInput testId={`lesson-video-${mi}-${li}`} value={l.video_url} onChange={v => updateLesson(mi, li, { video_url: v })} placeholder="https://youtu.be/... ou https://.../aula.mp4" />
                                                                                        </div>
                                                                                    </details>
                                                                                </div>
                                                                            </Field>

                                                                            <div>
                                                                                <div className="flex items-center justify-between mb-2">
                                                                                    <div className="overline text-[#5C5C5C]">Materiais complementares</div>
                                                                                    <button type="button" onClick={() => addResource(mi, li)} className="text-xs font-display text-[#1A1A1A] underline">+ Adicionar</button>
                                                                                </div>
                                                                                <div className="space-y-2">
                                                                                    {(l.resources || []).map((r, ri) => (
                                                                                        <div key={ri} className="grid grid-cols-1 md:grid-cols-[110px_1fr_1.5fr_auto] gap-2 items-center">
                                                                                            <select value={r.type} onChange={e => updateResource(mi, li, ri, { type: e.target.value })} className="imq-input !py-2">
                                                                                                <option value="pdf">PDF</option>
                                                                                                <option value="ebook">E-book</option>
                                                                                                <option value="meditation">Meditação</option>
                                                                                            </select>
                                                                                            <TextInput value={r.title} onChange={v => updateResource(mi, li, ri, { title: v })} placeholder="Título" />
                                                                                            <FileUploader
                                                                                                testId={`lesson-${mi}-${li}-resource-${ri}-uploader`}
                                                                                                value={r.url}
                                                                                                onChange={(url, originalName) => {
                                                                                                    const patch = { url };
                                                                                                    if (originalName) {
                                                                                                        if (!r.title) patch.title = stripExt(originalName);
                                                                                                        patch.type = inferResourceType(originalName);
                                                                                                    }
                                                                                                    updateResource(mi, li, ri, patch);
                                                                                                }}
                                                                                            />
                                                                                            <button type="button" onClick={() => removeResource(mi, li, ri)} className="text-red-600 hover:text-red-700 justify-self-end md:justify-self-center"><X className="w-4 h-4" /></button>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {error && <div data-testid="journey-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>}

                    <div className="flex justify-end gap-3 pt-2 border-t border-black/5">
                        <button onClick={() => setOpen(false)} className="btn-ghost">Cancelar</button>
                        <button onClick={save} data-testid="journey-save" className="btn-primary">Salvar jornada</button>
                    </div>
                </div>
            </Modal>
        </AdminLayout>
    );
}
